import { useState, useEffect } from "react";
import { fetchContactInfo, resolveAssetPath } from "../../api";

interface ChatChannel {
  id: string;
  name: string;
  icon: string;
  qrCode: string;
  label: string;
  color: string;
}

const defaultChannels: ChatChannel[] = [
  {
    id: "wechat",
    name: "WeChat",
    icon: "ri-wechat-line",
    qrCode:
      "",
    label: "Scan to add us on WeChat",
    color: "#07C160",
  },
  {
    id: "whatsapp",
    name: "WhatsApp",
    icon: "ri-whatsapp-line",
    qrCode:
      "",
    label: "Scan to chat on WhatsApp",
    color: "#25D366",
  },
  {
    id: "telegram",
    name: "Telegram",
    icon: "ri-telegram-line",
    qrCode:
      "",
    label: "Scan to chat on Telegram",
    color: "#0088CC",
  },
];

export default function FloatChatSupport() {
  const [channels, setChannels] = useState<ChatChannel[]>(defaultChannels);
  const [activeChannel, setActiveChannel] = useState<ChatChannel | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    fetchContactInfo()
      .then((data) => {
        setChannels((prev) =>
          prev.map((ch) => {
            if (ch.id === "wechat" && data.wechat_qr) return { ...ch, qrCode: resolveAssetPath(data.wechat_qr) || data.wechat_qr };
            if (ch.id === "whatsapp" && data.whatsapp_qr) return { ...ch, qrCode: resolveAssetPath(data.whatsapp_qr) || data.whatsapp_qr };
            if (ch.id === "telegram" && data.telegram_qr) return { ...ch, qrCode: resolveAssetPath(data.telegram_qr) || data.telegram_qr };
            return ch;
          })
        );
      })
      .catch(() => {});
  }, []);

  return (
    <>
      {/* Floating Chat Buttons */}
      <div className="fixed right-4 md:right-6 top-1/2 -translate-y-1/2 z-50 flex flex-col items-end gap-3">
        {/* Expanded icons */}
        {isOpen && (
          <div className="flex flex-col items-end gap-3 animate-in fade-in slide-in-from-bottom-4 duration-200">
            {channels.map((channel) => (
              <button
                key={channel.id}
                onClick={() => setActiveChannel(channel)}
                className="group flex items-center gap-2"
                aria-label={channel.name}
              >
                <span className="text-sm font-medium text-white bg-dark/80 px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                  {channel.name}
                </span>
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-white shadow-sm transition-transform hover:scale-110"
                  style={{ backgroundColor: channel.color }}
                >
                  <i className={`${channel.icon} text-xl`}></i>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Toggle button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`w-14 h-14 rounded-full flex items-center justify-center text-white shadow-lg transition-all duration-300 hover:scale-105 ${
            isOpen ? "bg-dark rotate-45" : "bg-primary"
          }`}
          aria-label="Toggle chat support"
        >
          <i className="ri-customer-service-2-line text-2xl"></i>
        </button>
      </div>

      {/* QR Code Modal */}
      {activeChannel && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => setActiveChannel(null)}
        >
          <div
            className="bg-white rounded-2xl p-6 md:p-8 mx-4 w-full max-w-sm animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white"
                  style={{ backgroundColor: activeChannel.color }}
                >
                  <i className={`${activeChannel.icon} text-lg`}></i>
                </div>
                <h3 className="text-lg font-bold text-dark">
                  {activeChannel.name}
                </h3>
              </div>
              <button
                onClick={() => setActiveChannel(null)}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors"
                aria-label="Close"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            {/* QR Code */}
            <div className="flex flex-col items-center gap-4">
              <div className="w-[220px] h-[220px] md:w-[260px] md:h-[260px] rounded-xl overflow-hidden bg-gray-50 border border-gray-100 flex items-center justify-center">
                <img
                  src={activeChannel.qrCode}
                  alt={`${activeChannel.name} QR code`}
                  className="w-full h-full object-cover"
                />
              </div>
              <p className="text-sm text-gray-500 text-center">
                {activeChannel.label}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}